import {
  GroundTruthData,
  GroundTruthDataListView,
  GroundTruthField,
  ReviewEntry,
  ReviewResult,
  ReviewStatus,
} from "src/store/modelLifeCycleGroundTruthSlice";
import { LabelHierarchy, Model } from "src/store/modelManagementSlice";
import { compareStringsIgnoreCase } from "src/utils/stringUtil";
import { isMultipleAnswerEqual } from "src/components/pages/audit/helpers";
import {
  INADEQUATE_INFORMATION,
  NO_OBJECT_INVOLVED,
  HIERARCHY_SEPARATOR,
  OTHER,
} from "src/constants";
import { isAdminUser } from "src/utils/permissionUtil";

export const getAnswersSeparatedByComma = (answer: string) => {
  return answer.includes("|") ? answer.split("|").join(", ") : answer;
};

export const isFieldMultiSelected = (groundTruthField: GroundTruthField, model: Model) => {
  return (
    Object.values(model.fields).find((field) => field.name === groundTruthField.fieldName)?.many ||
    false
  );
};

export const getConfirmedAnswer = (
  groundTruthField: GroundTruthField,
  value: string,
  many: boolean,
) => {
  if (value === ReviewResult.BOTH_CORRECT) {
    return groundTruthField.llmAnswer;
  } else if (value === ReviewResult.NEITHER) {
    return many ? [] : ReviewResult.NEITHER;
  } else {
    return value;
  }
};

export const isAnswerInadequate = (answer: string) => {
  return (
    !answer ||
    compareStringsIgnoreCase(answer, "none") ||
    compareStringsIgnoreCase(answer, INADEQUATE_INFORMATION)
  );
};

export const getParentValue = (str: string): string =>
  str.includes("---") ? str.split("---")[0] : str;

export const getChildValue = (str: string): string =>
  str.includes("---") ? str.split("---").pop() || "" : str;

export const isChildField = (str: string): boolean => str.includes("---");

export const groupByParent = (answers: string[]): { [key: string]: string[] } => {
  console.log(`🔍 [groupByParent] Input answers:`, answers);

  const result = answers.reduce((acc: { [key: string]: string[] }, item) => {
    const parentValue = getParentValue(item);
    const childValue = getChildValue(item);

    console.log(`🔍 [groupByParent] Processing item: "${item}"`);
    console.log(`🔍 [groupByParent] - Parent value: "${parentValue}"`);
    console.log(`🔍 [groupByParent] - Child value: "${childValue}"`);

    // Use lowercase key for case-insensitive grouping
    const parentKey = parentValue.toLowerCase();

    if (!acc[parentKey]) acc[parentKey] = [];
    acc[parentKey].push(childValue);

    return acc;
  }, {});

  console.log(`🔍 [groupByParent] Final grouped result:`, result);
  return result;
};

export const isAnswerGroupValid = (answers: string[]): boolean => {
  const hasInadequateInfo = answers.some((item) =>
    compareStringsIgnoreCase(item, INADEQUATE_INFORMATION),
  );

  const hasNoObjectInvolved = answers.some((item) =>
    compareStringsIgnoreCase(item, NO_OBJECT_INVOLVED),
  );

  if (hasInadequateInfo || hasNoObjectInvolved) {
    return answers.length === 1;
  }

  return answers.every((item) => item && !item.toLowerCase().includes("neither"));
};

export const isFieldConfirmedAnswerValid = (answer: string | string[]): boolean => {
  if (!Array.isArray(answer)) {
    const valueToCheck = getChildValue(answer);
    return valueToCheck !== "" && !valueToCheck.toLowerCase().includes("neither");
  }

  if (answer.length === 0) return false;

  if (!isChildField(answer[0])) {
    return isAnswerGroupValid(answer);
  }

  const groupedAnswers = groupByParent(answer);
  return Object.values(groupedAnswers).every(isAnswerGroupValid);
};

const isSpecialAnswer = (answer: string): boolean => {
  return (
    compareStringsIgnoreCase(answer, INADEQUATE_INFORMATION) ||
    compareStringsIgnoreCase(answer, NO_OBJECT_INVOLVED)
  );
};

const validateChildAnswers = (
  childAnswers: string[],
  parentAnswers: string[],
  fieldName: string,
): boolean => {
  console.log(`🔍 [validateChildAnswers] Starting child validation for field: ${fieldName}`);
  console.log(`🔍 [validateChildAnswers] Child answers:`, childAnswers);
  console.log(`🔍 [validateChildAnswers] Parent answers:`, parentAnswers);

  // If no answers, it's invalid
  if (childAnswers.length === 0) {
    console.log(`🔍 [validateChildAnswers] ❌ No child answers - INVALID`);
    return false;
  }

  // Group child answers by parent (case-insensitive)
  const groupedByParent = groupByParent(childAnswers);
  console.log(`🔍 [validateChildAnswers] Grouped child answers by parent:`, groupedByParent);

  // For each parent answer, validate its child answers
  for (const parentAnswer of parentAnswers) {
    console.log(`🔍 [validateChildAnswers] Validating parent: "${parentAnswer}"`);

    // Use lowercase key for case-insensitive lookup
    const parentKey = parentAnswer.toLowerCase();
    const childGroup = groupedByParent[parentKey];
    console.log(
      `🔍 [validateChildAnswers] Child group for "${parentAnswer}" (key "${parentKey}"):`,
      childGroup,
    );

    // Each parent must have at least one child answer
    if (!childGroup || childGroup.length === 0) {
      console.log(
        `🔍 [validateChildAnswers] ❌ No child group for parent "${parentAnswer}" - INVALID`,
      );
      return false;
    }

    // If any child is special answer, it should be the only child for that parent
    const hasSpecialChild = childGroup.some(
      (child) => isSpecialAnswer(child), // child is already the child value portion
    );
    console.log(
      `🔍 [validateChildAnswers] Has special child for "${parentAnswer}":`,
      hasSpecialChild,
    );

    if (hasSpecialChild && childGroup.length > 1) {
      console.log(`🔍 [validateChildAnswers] ❌ Special child with multiple children - INVALID`);
      return false;
    }

    // Since we now enforce the {parent}---{child} pattern consistently,
    // all child values are valid by default after grouping
    console.log(`🔍 [validateChildAnswers] All children valid for "${parentAnswer}": ✅ YES`);
  }

  console.log(`🔍 [validateChildAnswers] ✅ All validations passed - VALID`);
  return true;
};

const validateField = (
  field: GroundTruthField,
  groundTruth: GroundTruthData,
  model: Model,
): boolean => {
  console.log(`🔍 [validateField] Starting validation for field: ${field.fieldName}`);
  console.log(`🔍 [validateField] Field confirmed answer:`, field.confirmedAnswer);

  const hierarchyRule = model.hierarchy?.find((h) => h.childFieldName === field.fieldName);
  console.log(`🔍 [validateField] Hierarchy rule found:`, hierarchyRule ? "YES" : "NO");

  // If not a child field, use regular validation
  if (!hierarchyRule) {
    console.log(`🔍 [validateField] Not a child field - using regular validation`);
    const isValid = isFieldConfirmedAnswerValid(field.confirmedAnswer);
    console.log(`🔍 [validateField] Regular validation result for ${field.fieldName}:`, isValid);
    return isValid;
  }

  console.log(
    `🔍 [validateField] Child field detected - parent field: ${hierarchyRule.parentFieldName}`,
  );

  // Find parent field
  const parentField = groundTruth.fields.find((f) => f.fieldName === hierarchyRule.parentFieldName);
  console.log(`🔍 [validateField] Parent field found:`, parentField ? "YES" : "NO");
  console.log(`🔍 [validateField] Parent field confirmed answer:`, parentField?.confirmedAnswer);

  // If no parent field or no parent answer, invalid
  if (!parentField?.confirmedAnswer) {
    console.log(`🔍 [validateField] ❌ No parent field or parent answer - validation FAILED`);
    return false;
  }

  const parentAnswers = Array.isArray(parentField.confirmedAnswer)
    ? parentField.confirmedAnswer
    : [parentField.confirmedAnswer];

  let childAnswers = Array.isArray(field.confirmedAnswer)
    ? field.confirmedAnswer
    : [field.confirmedAnswer];

  console.log(`🔍 [validateField] Parent answers:`, parentAnswers);
  console.log(`🔍 [validateField] Child answers (original):`, childAnswers);

  // Debug: Show expected format for hierarchical answers
  console.log(`🔍 [validateField] Expected hierarchical format examples:`);
  parentAnswers.forEach((parentAnswer) => {
    childAnswers.forEach((childAnswer) => {
      const expectedFormat = `${parentAnswer}---${childAnswer}`;
      console.log(`🔍 [validateField] Expected format: "${expectedFormat}"`);
    });
  });

  // Debug: Show available child options from hierarchy mapping
  if (hierarchyRule && hierarchyRule.options) {
    console.log(
      `🔍 [validateField] Available hierarchy options for ${hierarchyRule.parentFieldName}:`,
      hierarchyRule.options,
    );
    parentAnswers.forEach((parentAnswer) => {
      const childOptions = hierarchyRule.options[parentAnswer] || [];
      console.log(`🔍 [validateField] Child options for "${parentAnswer}":`, childOptions);

      // Check if our child answer exists in the available options (case-insensitive)
      childAnswers.forEach((childAnswer) => {
        const isChildInOptions = childOptions.some((option) =>
          compareStringsIgnoreCase(option, childAnswer),
        );
        console.log(
          `🔍 [validateField] Is "${childAnswer}" in child options for "${parentAnswer}":`,
          isChildInOptions ? "✅ YES" : "❌ NO",
        );

        // Show what the expected hierarchical format should be
        if (isChildInOptions) {
          const expectedHierarchicalFormat = `${parentAnswer}---${childAnswer}`;
          console.log(
            `🔍 [validateField] ✅ Expected hierarchical format for validation: "${expectedHierarchicalFormat}"`,
          );
        }
      });
    });
  }

  const result = validateChildAnswers(childAnswers, parentAnswers, field.fieldName);
  console.log(
    `🔍 [validateField] Final validation result for ${field.fieldName}:`,
    result ? "✅ VALID" : "❌ INVALID",
  );

  return result;
};

export const isGroundTruthConfirmedAnswerValid = (
  groundTruth: GroundTruthData,
  model: Model,
): boolean => {
  return groundTruth.fields.every((field) => validateField(field, groundTruth, model));
};

export const isAnswerMatch = (field: GroundTruthField, many: boolean) => {
  if (isAnswerInadequate(field.userAnswer) || isAnswerInadequate(field.llmAnswer)) {
    return false;
  }
  return (
    compareStringsIgnoreCase(field.userAnswer, field.llmAnswer) ||
    (many && isMultipleAnswerEqual(field.userAnswer, field.llmAnswer))
  );
};

export const getLabel = (groundTruthField: GroundTruthField, type: ReviewResult): string => {
  switch (type) {
    case ReviewResult.BOTH_CORRECT:
      return ReviewResult.BOTH_CORRECT;
    case ReviewResult.LLM_CORRECT:
      return getAnswersSeparatedByComma(groundTruthField.llmAnswer);
    case ReviewResult.INADEQUATE:
      return INADEQUATE_INFORMATION;
    case ReviewResult.HUMAN_CORRECT:
      return `${getAnswersSeparatedByComma(groundTruthField.userAnswer)}`;
    default:
      return "Others (Expand full list)";
  }
};

export const getChildOptions = (
  hierarchy: LabelHierarchy[] | undefined,
  parentFieldName: string,
  childFieldName: string,
  parentValue: string | string[],
  hasInadequateInfo: boolean,
  hasOther: boolean,
): string[] => {
  if (!hierarchy) return [];
  const hierarchyRule = hierarchy.find(
    (h) => h.parentFieldName === parentFieldName && h.childFieldName === childFieldName,
  );

  if (!hierarchyRule) return [];

  let baseOptions: string[] = [];

  if (Array.isArray(parentValue)) {
    baseOptions = parentValue.reduce((acc: string[], value: string) => {
      const options = (hierarchyRule.options[value] || []).map(
        (childValue) => `${value}${HIERARCHY_SEPARATOR}${childValue}`,
      );
      return [...acc, ...options];
    }, []);
  } else {
    baseOptions = (hierarchyRule.options[parentValue] || []).map(
      (childValue) => `${parentValue}${HIERARCHY_SEPARATOR}${childValue}`,
    );
  }

  // Create a new array with all options, adding parent info for special cases
  return [
    ...baseOptions,
    ...(hasInadequateInfo ? [`${parentValue}${HIERARCHY_SEPARATOR}${INADEQUATE_INFORMATION}`] : []),
    ...(hasOther ? [`${parentValue}${HIERARCHY_SEPARATOR}${OTHER}`] : []),
  ];
};

export const getGroundTruthDataReviewStatus = (groundTruthData: GroundTruthData, alias: string) => {
  return groundTruthData.reviewContributors?.includes(alias) ||
    groundTruthData.status == ReviewStatus.REVIEWED
    ? ReviewStatus.REVIEWED
    : ReviewStatus.UNREVIEWED;
};

export const getOptionLabel = (option: string): string => {
  if (!option?.trim()) return "";

  if (option.includes(HIERARCHY_SEPARATOR)) {
    const parts = option.split(HIERARCHY_SEPARATOR).filter(Boolean);
    return parts.length ? parts[parts.length - 1] : "";
  }

  return option;
};

/**
 * Checks if ALL fields in the GroundTruthData have no llmClarification
 * @param groundTruthData The GroundTruthData to check
 * @returns true if none of the fields have llmClarification, false if any field has llmClarification
 */
export const hasNoLLMClarification = (groundTruthData: GroundTruthData): boolean => {
  if (!groundTruthData.fields || groundTruthData.fields.length === 0) {
    return true;
  }

  // Return true only if every field has no llmClarification
  return groundTruthData.fields.every(
    (field) => !field.llmClarification || field.llmClarification.trim() === "",
  );
};

export const isAssignedBasedReview = (model: Model) => {
  // Check both requiredReviewerCount (legacy, to be deprecated) and metadata.assign_based_review (new preferred method)
  return (
    (model.requiredReviewerCount && model.requiredReviewerCount >= 1) ||
    model.metadata?.assign_based_review?.toLowerCase() === "true"
  );
};

export const isCurrentUserAdminReview = (
  groundTruthData: GroundTruthData,
  model: Model,
  alias: string,
) => {
  return isAssignedBasedReview(model) && !groundTruthData.adminReviewers?.includes(alias);
};

export const hasReviewHistory = (groundTruthData: GroundTruthData): boolean =>
  !!groundTruthData.reviewHistory?.length;

/**
 * Filters review history entries based on admin assignment timestamp
 * @param reviewHistory The review history entries to filter
 * @param metadata The metadata containing admin assignment timestamps
 * @param alias The current user's alias
 * @returns Filtered review history entries
 */
export const filterReviewHistoryByAdminTimestamp = (
  reviewHistory: ReviewEntry[],
  metadata?: Record<string, any>,
  alias?: string,
): ReviewEntry[] => {
  if (!reviewHistory || !metadata || !alias) {
    return reviewHistory || [];
  }

  const adminAssignmentTimestamp = metadata[`${alias}_admin_assignment_timestamp`];
  if (!adminAssignmentTimestamp) {
    return reviewHistory;
  }

  return reviewHistory.filter((entry) => {
    // Only show entries submitted before the admin's assignment timestamp
    return new Date(entry.reviewSubmittedAt) < new Date(adminAssignmentTimestamp);
  });
};

/**
 * Checks if there are any review history entries to show after filtering by admin assignment timestamp
 * @param groundTruthData The ground truth data containing review history
 * @param ldap The current user's ldap
 * @param alias The current user's alias
 * @returns True if there are review history entries to show, false otherwise
 */
export const hasFilteredReviewHistory = (
  groundTruthData: GroundTruthData,
  ldap: string[],
  alias: string,
): boolean => {
  if (!hasReviewHistory(groundTruthData)) {
    return false;
  }

  const filteredHistory = filterReviewHistoryByAdminTimestamp(
    groundTruthData.reviewHistory || [],
    groundTruthData.metadata,
    alias,
  );

  return filteredHistory.length > 0;
};

export const showReviewDistribution = (
  groundTruthData: GroundTruthData,
  ldap: string[],
  alias: string,
): boolean =>
  hasFilteredReviewHistory(groundTruthData, ldap, alias) &&
  (isAdminUser(ldap) || groundTruthData.adminReviewers?.includes(alias) || false);

export const findNextUnreviewedCase = (
  list: GroundTruthDataListView[],
  alias: string,
  sourceId?: string,
) => {
  const currentIndex = sourceId ? list.findIndex((item) => item.sourceId === sourceId) : -1;

  const findCase = (items: GroundTruthDataListView[]) => {
    if (alias) {
      // If alias provided, first try to find a case where alias is in pendingReviewers
      const caseWithPendingReviewer = items.find((item) => item.pendingReviewers?.includes(alias));

      // If found a case with pending reviewer, return it
      if (caseWithPendingReviewer) {
        return caseWithPendingReviewer;
      }
    }

    // If no alias provided or no pending reviewer case found, look for unreviewed cases
    return items.find((item) => item.status === ReviewStatus.UNREVIEWED);
  };

  return (
    // Try to find case after current index
    (currentIndex !== -1 && findCase(list.slice(currentIndex + 1))) ||
    // If not found, find any case excluding current sourceId
    findCase(list.filter((item) => item.sourceId !== sourceId))
  );
};

// Helper functions for assignment-based review columns
export const getReviewedBy = (item: GroundTruthDataListView): string => {
  // Get reviewContributors who are NOT admin reviewers
  const reviewContributors = item.reviewContributors || [];
  const adminReviewers = item.adminReviewers || [];
  const regularReviewers = reviewContributors.filter(
    (contributor) => !adminReviewers.includes(contributor),
  );
  return regularReviewers.join(", ");
};

export const getValidatedBy = (item: GroundTruthDataListView): string => {
  // Get reviewContributors who ARE admin reviewers
  const reviewContributors = item.reviewContributors || [];
  const adminReviewers = item.adminReviewers || [];
  const validators = reviewContributors.filter((contributor) =>
    adminReviewers.includes(contributor),
  );
  return validators.join(", ");
};

export const getPendingReviewers = (item: GroundTruthDataListView): string => {
  // Get pendingReviewers who are NOT admin reviewers
  const pendingReviewers = item.pendingReviewers || [];
  const adminReviewers = item.adminReviewers || [];
  const regularPending = pendingReviewers.filter((pending) => !adminReviewers.includes(pending));
  return regularPending.join(", ");
};

export const getPendingValidators = (item: GroundTruthDataListView): string => {
  // Get users who are in both pendingReviewers AND adminReviewers
  const pendingReviewers = item.pendingReviewers || [];
  const adminReviewers = item.adminReviewers || [];
  const pendingValidators = pendingReviewers.filter((pending) => adminReviewers.includes(pending));
  return pendingValidators.join(", ");
};
